import React, { useState, useEffect, useCallback } from 'react';

import {
	FormControlLabel,
	Radio,
	RadioGroup,
	Select,
	MenuItem,
	TextField,
	Autocomplete,
	ToggleButton,
	ToggleButtonGroup,
	Box,
} from '@mui/material';
import { MiniLoading, Text, useIsFeatureEnabled } from '@innovyze/stylovyze';

import { DarkInfoTooltip } from '@Components/deteriorationModel/configurationTab/DarkInfoTooltip';
import { useGlobalization } from '@Translations';
import { CurveMethod, WeibullDeclineReason, WeibullFormulation } from '@Types';
import { searchDeteriorationModelCurveConfigs } from '@Services/deteriorationModels.services';
import { CustomCurveEditor } from './CustomCurveEditor';
import { WeibullFallbackBanner } from '@Components/deteriorationModel/utils/WeibullFallbackBanner';
import { getWeibullDeclineNote } from '@Components/deteriorationModel/utils/weibullDecline';
import { useCurveConfig } from './hooks';
import { FAILURE_COUNT_THRESHOLD } from './constants';
import { selectDeteriorationModel } from '@Selectors';

interface CurveMethodRadioGroupProps {
	curveState: ReturnType<typeof useCurveConfig>;
	industryStandardCurves: {
		curves: { key: string; displayName: string }[];
		isLoading: boolean;
	};
	currentCurveConfigId: string;
	failureCount: number;
	modelId: string;
	isBulkMode?: boolean;
}

// Per-group 2P/3P Weibull formulation control, shown for the failure-definition and
// custom-curve methods (the only methods that support a 3-parameter fit).
const WeibullFormulationToggle = ({
	value,
	onChange,
	t,
}: {
	value: WeibullFormulation;
	onChange: (formulation: WeibullFormulation) => void;
	t: (key: string) => string;
}) => (
	<ToggleButtonGroup
		value={value}
		exclusive
		size="small"
		onChange={(_, newFormulation: WeibullFormulation | null) => {
			if (newFormulation) onChange(newFormulation);
		}}
		style={{ paddingLeft: 24, marginTop: 8 }}>
		<ToggleButton value={WeibullFormulation.TWO_PARAMETER}>
			<Text>{t('2-Parameter (β, η)')}</Text>
		</ToggleButton>
		<ToggleButton value={WeibullFormulation.THREE_PARAMETER}>
			<Text>{t('3-Parameter (β, η, c)')}</Text>
		</ToggleButton>
	</ToggleButtonGroup>
);

const BannerBox = ({ children }: { children: React.ReactNode }) => (
	<Box style={{ paddingLeft: 24, marginTop: 8 }}>{children}</Box>
);

/**
 * Why this group has no curve at all.
 *
 * Prefers the reason the backend recorded over the sentence below. `noCurveFitted` only means
 * "the infer call resolved with zero fits" and carries no cause, so hardcoding one here would be
 * the exact guess `weibullDecline` warns against: it agrees with the stored reason today only
 * because both trainers share one failure-count gate and the 2-parameter fit is unguarded, which
 * is a property of the trainer module rather than of the contract.
 *
 * This is also the only surface that can show `insufficient_failures`. That reason implies fewer
 * usable failures than the shared minimum, so both trainers decline and there is never a fit to
 * fall back to — which is why its message must not claim any curve was used.
 */
const NoCurveFittedBanner = ({
	t,
	declineReason,
}: {
	t: (key: string) => string;
	declineReason?: WeibullDeclineReason;
}) => {
	const note = getWeibullDeclineNote(declineReason, t);

	return (
		<BannerBox>
			<WeibullFallbackBanner
				severity={note?.severity}
				message={
					note?.message ??
					// "Usable", not "recorded": the Assets with Failures count includes failures the trainers
					// skip for a non-positive age, so a group can show 8 there and still be under the minimum.
					t('No curve could be fitted for this asset group — it does not have enough usable failures.')
				}
			/>
		</BannerBox>
	);
};

// Preview controls for the failure-definition method. "No fits at all" is not a 3-parameter
// concern — the group already passed the failure-count gate — so that banner renders regardless
// of the flag. Only the 2P/3P toggle is gated behind it.
//
// Messages about the drawn curve — the 2P fallback, a flat fit, and the fitted coefficients — live
// under the chart in `CurveFitNotes`, not here: stacking them into this panel pushed most of the
// curve methods out of view behind a scrollbar.
const FailureDefinitionSection = ({
	curveState,
	isWeibull3ParamEnabled,
	t,
}: {
	curveState: ReturnType<typeof useCurveConfig>;
	isWeibull3ParamEnabled: boolean;
	t: (key: string) => string;
}) => {
	if (curveState.curveConfig.inProcess || curveState.failureDefinitionCurveLoading) {
		return <MiniLoading loading />;
	}

	return (
		<>
			{isWeibull3ParamEnabled && (
				<WeibullFormulationToggle
					value={curveState.weibullFormulation}
					onChange={curveState.setWeibullFormulation}
					t={t}
				/>
			)}
			{curveState.noCurveFitted && (
				<NoCurveFittedBanner t={t} declineReason={curveState.selectedFormulationDeclineReason} />
			)}
		</>
	);
};

type IndustryStandardCurves = {
	curves: { key: string; displayName: string }[];
	isLoading: boolean;
};

const TwoParameterOnlyNote = ({ t }: { t: (key: string) => string }) => (
	<Text style={{ paddingLeft: 24, color: '#666' }}>{t('2-parameter only')}</Text>
);

const IndustryCurveSection = ({
	curveState,
	industryStandardCurves,
	isWeibull3ParamEnabled,
	t,
}: {
	curveState: ReturnType<typeof useCurveConfig>;
	industryStandardCurves: IndustryStandardCurves;
	isWeibull3ParamEnabled: boolean;
	t: (key: string) => string;
}) => {
	if (industryStandardCurves.isLoading || curveState.curveConfig.inProcess || curveState.industryStandardCurveLoading) {
		return <MiniLoading loading />;
	}

	return (
		<>
			<Select
				value={curveState.industryStandardCurve || ''}
				onChange={e => curveState.setIndustryStandardCurve(e.target.value)}>
				{industryStandardCurves.curves.map(curve => (
					<MenuItem key={curve.key} value={curve.key}>
						<Text>{curve.displayName}</Text>
					</MenuItem>
				))}
			</Select>
			<Text style={{ color: '#666', fontSize: '12px' }}>
				{t('Industry standard curves are based on failure data from 500+ utilities in the U.S.')}
			</Text>
			{isWeibull3ParamEnabled && <TwoParameterOnlyNote t={t} />}
		</>
	);
};

const CopiedCurveSection = ({
	curveState,
	searchResults,
	inputValue,
	setInputValue,
	currentCurveConfigId,
	t,
}: {
	curveState: ReturnType<typeof useCurveConfig>;
	searchResults: { isLoading: boolean; curveConfigs: { curveConfigId: string; assetGroupName: string }[] };
	inputValue: string;
	setInputValue: (value: string) => void;
	currentCurveConfigId: string;
	t: (key: string) => string;
}) => {
	if (curveState.curveConfig.inProcess || curveState.copiedCurveLoading) {
		return <MiniLoading loading />;
	}

	return (
		<>
			<Autocomplete
				loading={searchResults.isLoading}
				options={searchResults.curveConfigs
					.filter(curve => curve.curveConfigId !== currentCurveConfigId)
					.map(curve => ({
						curveConfigId: curve.curveConfigId,
						assetGroupName: curve.assetGroupName,
					}))}
				getOptionLabel={option => option.assetGroupName}
				renderOption={(props, option) => (
					<li {...props} key={option.curveConfigId}>
						<div>
							<Text variant="body2" fontWeight="bold">
								{option.assetGroupName}
							</Text>
						</div>
					</li>
				)}
				noOptionsText={inputValue.trim() ? t('No matching asset groups found') : t('Type to search')}
				isOptionEqualToValue={(option, value) => option.curveConfigId === value.curveConfigId}
				value={searchResults.curveConfigs.find(curve => curve.curveConfigId === curveState.copyCurve) || null}
				onChange={(_, newValue: { curveConfigId: string; assetGroupName: string } | null) => {
					curveState.setCopyCurve(newValue?.curveConfigId || null);
				}}
				inputValue={inputValue}
				onInputChange={(_, newInputValue) => setInputValue(newInputValue)}
				renderInput={params => (
					<TextField
						{...params}
						label={t('Search and select asset group')}
						variant="outlined"
						placeholder={t('Type to search...')}
					/>
				)}
			/>
			<Text style={{ color: '#666', fontSize: '12px' }}>
				{t('Only groups with defined survival curve method will be displayed')}
			</Text>
		</>
	);
};

const OneExpectedLifeSection = ({
	curveState,
	isWeibull3ParamEnabled,
	t,
}: {
	curveState: ReturnType<typeof useCurveConfig>;
	isWeibull3ParamEnabled: boolean;
	t: (key: string) => string;
}) => {
	if (curveState.curveConfig.inProcess) {
		return <MiniLoading loading />;
	}

	return (
		<>
			<TextField
				onChange={e => curveState.setOneExpectedUsefulLife(Number(e.target.value))}
				label={<Text>{t('Enter One Expected Useful Life (Years)')}</Text>}
				variant="outlined"
				value={curveState.oneExpectedUsefulLife || ''}
			/>
			<Text style={{ color: '#666', fontSize: '12px' }}>
				{t(
					'Generated curve will be a straight line that starts at 0 and ends at the one expected useful life. It will not appear in the chart',
				)}
			</Text>
			{isWeibull3ParamEnabled && <TwoParameterOnlyNote t={t} />}
		</>
	);
};

// Asset-group search for the copied-curve method: owns the query input, the search effect and
// the result list, so the radio group itself stays declarative.
const useAssetGroupSearch = (curveState: ReturnType<typeof useCurveConfig>, modelId: string) => {
	const [searchResults, setSearchResults] = useState({
		isLoading: false,
		curveConfigs: [] as { curveConfigId: string; assetGroupName: string }[],
	});
	const [inputValue, setInputValue] = useState('');

	const performSearch = useCallback(
		async (searchValue: string) => {
			if (searchValue.trim() && curveState.selectedCurveMethod === CurveMethod.COPIED_CURVE) {
				setSearchResults(prev => ({ ...prev, isLoading: true }));

				try {
					const response = await searchDeteriorationModelCurveConfigs({
						modelId,
						searchValue: searchValue.trim(),
						selectedCurveMethods: [
							CurveMethod.FAILURE_DEFINITION,
							CurveMethod.INDUSTRY_CURVE,
							CurveMethod.CUSTOM_CURVE,
							CurveMethod.ONE_EXPECTED_LIFE,
						],
					});

					const curveConfigs = response.data.data.map(curve => ({
						curveConfigId: curve._id,
						assetGroupName: curve.asset_group_name,
					}));

					setSearchResults({
						isLoading: false,
						curveConfigs,
					});
				} catch (error) {
					console.error('Failed to search curve configurations:', error);
					setSearchResults({
						isLoading: false,
						curveConfigs: [],
					});
				}
			} else if (!searchValue.trim()) {
				// Clear search results when input is empty
				setSearchResults({
					isLoading: false,
					curveConfigs: [],
				});
			}
		},
		[modelId, curveState.selectedCurveMethod],
	);

	useEffect(() => {
		// Initial load for COPIED_CURVE method
		if (curveState.selectedCurveMethod === CurveMethod.COPIED_CURVE) {
			if (curveState.copyCurve && curveState.copyCurveName) {
				setInputValue(curveState.copyCurveName);
			}
		}

		// Clear when switching away from COPIED_CURVE mode
		if (curveState.selectedCurveMethod !== CurveMethod.COPIED_CURVE) {
			curveState.setCopyCurve(null);
		}
	}, [curveState.selectedCurveMethod, curveState.copyCurveName, curveState.copyCurve]);

	// Trigger search when input value changes
	useEffect(() => {
		if (curveState.selectedCurveMethod === CurveMethod.COPIED_CURVE) {
			performSearch(inputValue);
		}
	}, [inputValue]);

	return { searchResults, inputValue, setInputValue };
};

export const CurveMethodRadioGroup = ({
	curveState,
	industryStandardCurves,
	currentCurveConfigId,
	failureCount,
	modelId,
	isBulkMode = false,
}: CurveMethodRadioGroupProps) => {
	const { t } = useGlobalization();
	const deteriorationModel = selectDeteriorationModel();
	const currentDeteriorationModel = deteriorationModel.deteriorationModel;
	// Coerced: the hook is typed to return `string | Object`, but every use here is a boolean gate.
	const isWeibull3ParamEnabled = !!useIsFeatureEnabled('info360-weibull-3-param');
	const { searchResults, inputValue, setInputValue } = useAssetGroupSearch(curveState, modelId);

	// Derived rather than held in state behind an effect. Storing the English text and translating
	// it at the call site hid both strings from i18next-parser -- neither reached any catalogue, so
	// de/fr/ja users read English (including the `defiunition` typo that survived here), and
	// `check:translations` could not see either problem. It also ran `t(null)` on the first frame,
	// before the effect had set anything.
	const failureDefinitionHelpText = !currentDeteriorationModel?.failureDefinitionId
		? t(
				'No failure definition added to the model. You can add a failure definition above, or select a different option to define curve.',
		  )
		: failureCount < FAILURE_COUNT_THRESHOLD
		? t('Not enough failures to generate curve (<5 failures). Select a different method for this group.')
		: null;

	useEffect(() => {
		if (curveState.selectedCurveMethod === CurveMethod.CUSTOM_CURVE && curveState.customCurvePoints.length === 0) {
			curveState.setCustomCurvePoints([
				{ age: null, survivalProbability: null },
				{ age: null, survivalProbability: null },
			]);
		}
	}, [curveState.selectedCurveMethod]);

	const isFailureDefinitionDisabled = failureCount < FAILURE_COUNT_THRESHOLD;

	return (
		<RadioGroup
			onChange={e => curveState.setSelectedCurveMethod(e.target.value as CurveMethod)}
			value={curveState.selectedCurveMethod}>
			{/* From Failure Definition */}
			{isFailureDefinitionDisabled ? (
				<DarkInfoTooltip
					heading={t('From Failure Definition')}
					body={
						isBulkMode
							? t(
									'At least 5 failures are required to generate a curve from failure definition. All selected groups must meet this requirement.',
							  )
							: failureDefinitionHelpText
					}>
					<span>
						<FormControlLabel
							value={CurveMethod.FAILURE_DEFINITION}
							control={<Radio disabled={isFailureDefinitionDisabled} />}
							label={
								<Text color={isFailureDefinitionDisabled ? 'textSecondary' : 'inherit'}>
									{t('From Failure Definition')}
								</Text>
							}
							disabled={isFailureDefinitionDisabled}
						/>
					</span>
				</DarkInfoTooltip>
			) : (
				<FormControlLabel
					value={CurveMethod.FAILURE_DEFINITION}
					control={<Radio />}
					label={<Text>{t('From Failure Definition')}</Text>}
				/>
			)}
			{curveState.selectedCurveMethod === CurveMethod.FAILURE_DEFINITION && (
				<FailureDefinitionSection curveState={curveState} isWeibull3ParamEnabled={isWeibull3ParamEnabled} t={t} />
			)}

			{/* Industry Standard Curve */}
			<FormControlLabel
				value={CurveMethod.INDUSTRY_CURVE}
				control={<Radio />}
				label={<Text>{t('Industry Standard Curve')}</Text>}
			/>
			{curveState.selectedCurveMethod === CurveMethod.INDUSTRY_CURVE && (
				<IndustryCurveSection
					curveState={curveState}
					industryStandardCurves={industryStandardCurves}
					isWeibull3ParamEnabled={isWeibull3ParamEnabled}
					t={t}
				/>
			)}

			{/* Custom Curve */}
			<FormControlLabel value={CurveMethod.CUSTOM_CURVE} control={<Radio />} label={<Text>{t('Custom Curve')}</Text>} />
			{curveState.selectedCurveMethod === CurveMethod.CUSTOM_CURVE && (
				<>
					<CustomCurveEditor
						customCurvePoints={curveState.customCurvePoints}
						setCustomCurvePoints={curveState.setCustomCurvePoints}
						isLoading={curveState.curveConfig.inProcess}
					/>
					{isWeibull3ParamEnabled && (
						<WeibullFormulationToggle
							value={curveState.weibullFormulation}
							onChange={curveState.setWeibullFormulation}
							t={t}
						/>
					)}
				</>
			)}

			{/* Copy Curve from another Group */}
			<FormControlLabel
				value={CurveMethod.COPIED_CURVE}
				control={<Radio />}
				label={<Text>{t('Copy Curve from another Group')}</Text>}
			/>
			{curveState.selectedCurveMethod === CurveMethod.COPIED_CURVE && (
				<CopiedCurveSection
					curveState={curveState}
					searchResults={searchResults}
					inputValue={inputValue}
					setInputValue={setInputValue}
					currentCurveConfigId={currentCurveConfigId}
					t={t}
				/>
			)}

			{/* One Expected Useful Life */}
			<FormControlLabel
				value={CurveMethod.ONE_EXPECTED_LIFE}
				control={<Radio />}
				label={<Text>{t('One Expected Useful Life')}</Text>}
			/>
			{curveState.selectedCurveMethod === CurveMethod.ONE_EXPECTED_LIFE && (
				<OneExpectedLifeSection curveState={curveState} isWeibull3ParamEnabled={isWeibull3ParamEnabled} t={t} />
			)}
		</RadioGroup>
	);
};
